import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, DatePicker, Form, Input, InputNumber, Select, Space, Steps, Typography, message } from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import pmvService from '@/services/pmv.service';
import { useProjects } from '@/hooks/useProjects';
import { formatProjectOption } from '@/utils/pmvDisplay';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const PmvRequestFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const [step, setStep] = useState(0);
  const { data: projects = [] } = useProjects();
  const classQ = useQuery({ queryKey: ['pmv-classifications'], queryFn: () => pmvService.listClassifications() });

  const reqQ = useQuery({
    queryKey: ['pmv-request', id],
    queryFn: () => pmvService.getRequest(id!),
    enabled: isEdit,
  });

  useEffect(() => {
    const req = reqQ.data;
    if (!req) return;
    if (req.status !== 'DRAFT') {
      message.warning('Only draft equipment requests can be edited');
      navigate(`/asset/pmv/requests/${req.id}`);
      return;
    }
    form.setFieldsValue({
      project_id: req.project_id,
      priority: req.priority ?? 'NORMAL',
      justification: req.justification ?? undefined,
    });
  }, [form, navigate, reqQ.data]);

  const createMut = useMutation({
    mutationFn: (values: Record<string, unknown>) => pmvService.createRequest(values),
    onSuccess: (data) => {
      message.success('Equipment request created');
      navigate(`/asset/pmv/requests/${data.id}`);
    },
    onError: (e: Error) => message.error(e.message),
  });

  const patchMut = useMutation({
    mutationFn: (values: Record<string, unknown>) => pmvService.patchRequest(id!, values),
    onSuccess: (data) => {
      message.success('Equipment request updated');
      navigate(`/asset/pmv/requests/${data.id}`);
    },
    onError: (e: Error) => message.error(e.message),
  });

  const classOptions = (classQ.data ?? [])
    .filter((c) => c.parent_id)
    .map((c) => ({ value: c.id, label: `${c.code} — ${c.name}` }));

  const projectOptions = projects.map((p) => ({
    value: p.id,
    label: formatProjectOption(p),
  }));

  const goNextCreate = async () => {
    if (step === 0) {
      await form.validateFields(['project_id', 'required_from', 'required_until']);
      setStep(1);
      return;
    }
    if (step === 1) {
      await form.validateFields(['classification_id', 'quantity_required']);
      setStep(2);
    }
  };

  const submitCreate = () => {
    form.validateFields().then((v) => {
      createMut.mutate({
        project_id: v.project_id,
        priority: v.priority ?? 'NORMAL',
        justification: v.justification,
        requirements: [
          {
            classification_id: v.classification_id,
            quantity_required: v.quantity_required ?? 1,
            required_from: (v.required_from as dayjs.Dayjs).format('YYYY-MM-DD'),
            required_until: (v.required_until as dayjs.Dayjs).format('YYYY-MM-DD'),
          },
        ],
      });
    });
  };

  const watchedProjectId = Form.useWatch('project_id', form);
  const watchedClassId = Form.useWatch('classification_id', form);
  const watchedFrom = Form.useWatch('required_from', form);
  const watchedUntil = Form.useWatch('required_until', form);
  const watchedQty = Form.useWatch('quantity_required', form);
  const watchedPriority = Form.useWatch('priority', form);
  const watchedJustification = Form.useWatch('justification', form);
  const reviewProject = projectOptions.find((p) => p.value === watchedProjectId)?.label ?? '—';
  const reviewClass = classOptions.find((c) => c.value === watchedClassId)?.label ?? '—';

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel={isEdit ? 'Edit Equipment Request' : 'New Equipment Request'}
          documentNumber={reqQ.data?.request_number ?? ''}
        />
      }
    >
      <Title level={5}>{isEdit ? 'Edit draft request' : 'Guided capability request'}</Title>

      {!isEdit && (
        <Steps
          current={step}
          style={{ marginBottom: 24, maxWidth: 720 }}
          items={[
            { title: 'Project & schedule' },
            { title: 'Equipment capability' },
            { title: 'Review & create' },
          ]}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 640 }}
        onFinish={(v) => {
          if (isEdit) {
            patchMut.mutate({
              project_id: v.project_id,
              priority: v.priority ?? 'NORMAL',
              justification: v.justification ?? null,
            });
          }
        }}
      >
        {isEdit ? (
          <>
            <Form.Item name="project_id" label="Project" rules={[{ required: true }]}>
              <Select showSearch optionFilterProp="label" options={projectOptions} />
            </Form.Item>
            <Form.Item name="priority" label="Priority" initialValue="NORMAL">
              <Select options={[{ value: 'NORMAL' }, { value: 'HIGH' }, { value: 'CRITICAL' }]} />
            </Form.Item>
            <Form.Item name="justification" label="Justification">
              <Input.TextArea rows={3} />
            </Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={patchMut.isPending}>
                Save
              </Button>
              <Button onClick={() => navigate(id ? `/asset/pmv/requests/${id}` : '/asset/pmv/requests')}>
                Cancel
              </Button>
            </Space>
          </>
        ) : (
          <>
            <div style={{ display: step === 0 ? 'block' : 'none' }}>
              <Form.Item name="project_id" label="Project" rules={[{ required: true }]}>
                <Select showSearch optionFilterProp="label" options={projectOptions} />
              </Form.Item>
              <Form.Item name="required_from" label="Required from" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="required_until" label="Required until" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="priority" label="Priority" initialValue="NORMAL">
                <Select options={[{ value: 'NORMAL' }, { value: 'HIGH' }, { value: 'CRITICAL' }]} />
              </Form.Item>
              <Form.Item name="justification" label="Justification">
                <Input.TextArea rows={3} />
              </Form.Item>
            </div>

            <div style={{ display: step === 1 ? 'block' : 'none' }}>
              <Form.Item name="classification_id" label="Classification" rules={[{ required: true }]}>
                <Select showSearch optionFilterProp="label" options={classOptions} />
              </Form.Item>
              <Form.Item name="quantity_required" label="Quantity" initialValue={1} rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </div>

            <div style={{ display: step === 2 ? 'block' : 'none' }}>
              <Space direction="vertical" size="small" style={{ marginBottom: 16 }}>
                <Text>
                  <strong>Project:</strong> {reviewProject}
                </Text>
                <Text>
                  <strong>Schedule:</strong>{' '}
                  {watchedFrom ? dayjs(watchedFrom).format('YYYY-MM-DD') : '—'} →{' '}
                  {watchedUntil ? dayjs(watchedUntil).format('YYYY-MM-DD') : '—'}
                </Text>
                <Text>
                  <strong>Capability:</strong> {reviewClass} × {watchedQty ?? 1}
                </Text>
                <Text>
                  <strong>Priority:</strong> {watchedPriority ?? 'NORMAL'}
                </Text>
                {watchedJustification ? (
                  <Text>
                    <strong>Justification:</strong> {watchedJustification}
                  </Text>
                ) : null}
              </Space>
            </div>

            <Space>
              {step > 0 && (
                <Button onClick={() => setStep((s) => s - 1)}>Back</Button>
              )}
              {step < 2 ? (
                <Button type="primary" onClick={() => void goNextCreate()}>
                  Next
                </Button>
              ) : (
                <Button type="primary" loading={createMut.isPending} onClick={submitCreate}>
                  Create Equipment Request
                </Button>
              )}
              <Button onClick={() => navigate('/asset/pmv/requests')}>Cancel</Button>
            </Space>
          </>
        )}
      </Form>
    </WorkspaceLayout>
  );
};

export default PmvRequestFormPage;
